import { z } from 'zod';
import { compareDecimal } from '../utils/decimal.js';
/** Bezeichner sind cuid2-Werte; die Länge ist auf 24 Zeichen konfiguriert. */
export const idSchema = z.string().min(8).max(64);
export const isoDateTimeSchema = z.string().datetime({ offset: true });
/** Kalenderdatum ohne Zeitanteil (`YYYY-MM-DD`). */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Erwartet wird YYYY-MM-DD.');
export const countryCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/, 'Erwartet wird ein ISO-3166-Alpha-2-Code.');
export const currencyCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{3}$/, 'Erwartet wird ein ISO-4217-Code.');
/**
 * Dezimalzahlen werden als String transportiert, damit die Genauigkeit der
 * Datenbank (Decimal) nicht über IEEE-754-Gleitkommazahlen verloren geht.
 */
export function decimalStringSchema(options) {
    const integerDigits = options.precision - options.scale;
    const pattern = new RegExp(`^-?\\d{1,${integerDigits}}(\\.\\d{1,${options.scale}})?$`);
    return z
        .string()
        .trim()
        .superRefine((value, ctx) => {
        if (!pattern.test(value)) {
            // Fatal is intentional: downstream refinements use the exact decimal
            // parser and must never receive malformed or exponential notation.
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Erwartet wird eine Dezimalzahl mit maximal ${options.scale} Nachkommastellen.`,
                fatal: true,
            });
            return z.NEVER;
        }
        if (options.min !== undefined && compareDecimal(value, String(options.min)) < 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Der Wert darf nicht kleiner als ${options.min} sein.`,
            });
        }
        if (options.max !== undefined && compareDecimal(value, String(options.max)) > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Der Wert darf nicht größer als ${options.max} sein.`,
            });
        }
    });
}
export const moneySchema = decimalStringSchema({ precision: 18, scale: 4 });
export const percentSchema = decimalStringSchema({ precision: 9, scale: 6, min: 0, max: 100 });
/** Mengen werden in Basiseinheit mit sechs Nachkommastellen geführt. */
export const quantitySchema = decimalStringSchema({ precision: 18, scale: 6 });
export const nonNegativeQuantitySchema = decimalStringSchema({ precision: 18, scale: 6, min: 0 });
export const positiveQuantitySchema = nonNegativeQuantitySchema.refine((value) => compareDecimal(value, '0') > 0, 'Die Menge muss größer als null sein.');
/** Leere Strings aus Formularen werden zu `null`, damit optionale Spalten leer bleiben. */
export function emptyToNull(schema) {
    return z.preprocess((value) => (typeof value === 'string' && value.trim().length === 0 ? null : value), schema.nullable());
}
/** `?status=ACTIVE,PROSPECT` — Mehrfachwerte kommen als kommaseparierte Liste. */
export function commaSeparated(schema) {
    return z.preprocess((value) => {
        if (typeof value !== 'string') {
            return value;
        }
        const entries = value
            .split(',')
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0);
        return entries.length === 0 ? undefined : entries;
    }, z.array(schema).optional());
}
//# sourceMappingURL=common.js.map